import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { MeResponse } from "@chat-yai/shared";
import { meApi } from "@/api/endpoints/me";
import { authApi } from "@/api/endpoints/auth";
import { authStorage } from "@/lib/auth-storage";
import { ApiError } from "@/api/client";
import { decodeJwt } from "@/lib/jwt";

interface SessionContextValue {
  me: MeResponse | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isImpersonating: boolean;
  impersonatorEmail: string | null;
  setAccessToken: (token: string | null) => void;
  refreshMe: () => Promise<MeResponse | null>;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient();
  const [tokenVersion, setTokenVersion] = useState(0);
  const [bootstrapping, setBootstrapping] = useState(true);

  // Subscribe to access-token changes so consumers re-render when sign-in state shifts.
  useEffect(() => {
    return authStorage.subscribe(() => setTokenVersion((v) => v + 1));
  }, []);

  // Try to silently refresh once on mount so /dashboard doesn't bounce to /login on reload.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authApi.refresh();
        if (!cancelled) authStorage.set(res.access_token);
      } catch {
        // ignore — anonymous
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const meQuery = useQuery({
    queryKey: ["me", tokenVersion],
    queryFn: () => meApi.get(),
    enabled: !!authStorage.get(),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 401) return false;
      return failureCount < 1;
    },
    staleTime: 30_000,
  });

  const setAccessToken = useCallback((token: string | null) => {
    authStorage.set(token);
  }, []);

  const refreshMe = useCallback(async () => {
    const res = await queryClient.fetchQuery({
      queryKey: ["me", tokenVersion],
      queryFn: () => meApi.get(),
    });
    return res ?? null;
  }, [queryClient, tokenVersion]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // ignore
    }
    authStorage.clear();
    queryClient.clear();
  }, [queryClient]);

  const claims = decodeJwt(authStorage.get());

  const value = useMemo<SessionContextValue>(
    () => ({
      me: meQuery.data ?? null,
      isLoading: bootstrapping || meQuery.isLoading,
      isAuthenticated: !!authStorage.get() && !!meQuery.data,
      isImpersonating: !!claims?.impersonation_session_id,
      impersonatorEmail: claims?.impersonator_email ?? null,
      setAccessToken,
      refreshMe,
      logout,
    }),
    [meQuery.data, meQuery.isLoading, bootstrapping, claims, setAccessToken, refreshMe, logout],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside SessionProvider");
  return ctx;
}
